import type { EnemyDefinition } from './world-types.js';

/** Combat Encounter Guidelines (GMG p. 22): the Monster Budget is the party's total levels; difficulty is a share of it. */
export type Difficulty = 'trivial' | 'easy' | 'medium' | 'hard' | 'deadly' | 'very-deadly';
export const DIFFICULTY_SHARE: Record<Exclude<Difficulty, 'trivial'>, number> = { easy: .5, medium: .75, hard: 1, deadly: 1.25, 'very-deadly': 1.5 };
export const DIFFICULTIES = ['easy', 'medium', 'hard', 'deadly', 'very-deadly'] as const;
/** Two minions per hero count as one monster of the hero's level (GMG p. 23), so a minion is half a hero level. */
export const MINION_LEVEL_SHARE = .5;
/** Authored elite fights may have one opponent per two heroes; swarms allow up to three monsters or four minions per hero. */
export const MONSTERS_PER_HERO = { min: .5, max: 3 };
export const MINIONS_PER_HERO_MAX = 4;

export function monsterBudget(heroLevels: readonly number[]): number { return heroLevels.reduce((sum, level) => sum + level, 0); }

/** What one opponent costs against the budget for a party whose heroes share `heroLevel`. */
export function enemyLevel(enemy: Pick<EnemyDefinition, 'kind' | 'level' | 'id'>, heroLevel: number): number {
  if (enemy.kind === 'minion') return heroLevel * MINION_LEVEL_SHARE;
  if (enemy.level === undefined) throw new Error(`Monster ${enemy.id} has no level.`);
  return enemy.level;
}
export function encounterLevels(enemies: readonly Pick<EnemyDefinition, 'kind' | 'level' | 'id'>[], heroLevel: number): number {
  return enemies.reduce((sum, enemy) => sum + enemyLevel(enemy, heroLevel), 0);
}

/** The share of the budget `classifyEncounter` files under each difficulty, [from, below); Very Deadly is open-ended, capped here at 175%. */
export const DIFFICULTY_BANDS: Record<Exclude<Difficulty, 'trivial'>, readonly [number, number]> = {
  easy: [.375, .625], medium: [.625, .875], hard: [.875, 1.125], deadly: [1.125, 1.375], 'very-deadly': [1.375, 1.75],
};

/** The band an encounter's cost falls in: the nearest of the book's 50 / 75 / 100 / 125 / 150% steps. */
export function classifyEncounter(levels: number, budget: number): Difficulty {
  const share = levels / budget;
  if (share < .375) return 'trivial';
  if (share < .625) return 'easy';
  if (share < .875) return 'medium';
  if (share < 1.125) return 'hard';
  if (share < 1.375) return 'deadly';
  return 'very-deadly';
}

/** Groups opponents by the encounter that engages together. Opponents with no group stand alone. */
export function encounterGroups<T extends Pick<EnemyDefinition, 'id' | 'encounterId'>>(enemies: readonly T[]): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  for (const enemy of enemies) { const id = enemy.encounterId ?? enemy.id; groups.set(id, [...groups.get(id) ?? [], enemy]); }
  return groups;
}
